import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useRouter } from '@tanstack/react-router';
import { ClipboardList, Pencil, Users } from 'lucide-react';
import { BackButton } from '@/components/common/BackButton';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatCount, formatNumber } from '@/utils/number';
import { sortBySortOrder } from '@/utils/sort';
import { surveyDetailQueryOptions } from '../api/surveys.api';
import { SurveyAnswerForm } from '../components/SurveyAnswerForm';
import { SurveyDetailHeader } from '../components/SurveyDetailHeader';
import { SurveyDetailSkeleton } from '../components/SurveyDetailSkeleton';
import { SurveyQuestionPreview } from '../components/SurveyQuestionPreview';

export function SurveyDetailPage() {
  const { surveyId } = useParams({ from: '/_auth/feedback/surveys/$surveyId/' });
  const router = useRouter();
  const {
    data: survey,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(surveyDetailQueryOptions(surveyId));

  const goBack = useCallback(() => {
    if (router.history.canGoBack()) router.history.back();
    else void router.navigate({ to: '/feedback/surveys' });
  }, [router]);

  const backButton = <BackButton onClick={goBack} />;

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5">
        {backButton}
        <SurveyDetailSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5">
        {backButton}
        <ErrorState
          error={error}
          fallbackTitle="Could not load this survey"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5">
        {backButton}
        <EmptyState
          icon={ClipboardList}
          title="Survey unavailable"
          description="This survey may have been deleted or the link is no longer valid."
          fill
        />
      </div>
    );
  }

  const sortedQuestions = sortBySortOrder(survey.questions);
  const responseCount = survey.responseCount ?? 0;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5">
      <PageHeader
        title={survey.title}
        leading={backButton}
        actions={
          <>
            <Link
              to="/feedback/surveys/$surveyId/submissions"
              params={{ surveyId }}
              className={cn(buttonVariants({ variant: 'default' }), 'gap-2')}
              aria-label={`View ${formatCount(responseCount, 'submission')}`}
            >
              <Users className="size-4" aria-hidden />
              View Submissions
              <Badge
                variant="secondary"
                className="h-5 min-w-5 justify-center px-1.5 text-3xs font-semibold tabular-nums"
              >
                {formatNumber(responseCount)}
              </Badge>
            </Link>
            <Link
              to="/feedback/surveys/$surveyId/edit"
              params={{ surveyId }}
              className={cn(buttonVariants({ variant: 'default' }), 'gap-2')}
            >
              <Pencil className="size-4" aria-hidden />
              Edit Survey
            </Link>
          </>
        }
      />

      <SurveyDetailHeader survey={survey} />

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Questions</h2>
          <span className="text-sm text-muted-foreground">{sortedQuestions.length} total</span>
        </div>
        {sortedQuestions.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="No questions"
            description="This survey has no questions yet."
          />
        ) : survey.status === 'active' ? (
          <SurveyAnswerForm survey={survey} />
        ) : (
          <ol className="flex flex-col gap-3">
            {sortedQuestions.map((question, index) => (
              <li key={question.id}>
                <SurveyQuestionPreview question={question} index={index} />
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
